import { useId } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'destructive' | 'default'
  /** Optional className override for the confirm button — for accent colors not covered by variant. */
  confirmClassName?: string
  loading?: boolean
  /** #411 — Confirm (and the input's Enter) stays disabled, with no spinner; Cancel and Escape still close.
   *  `loading` instead locks the whole dialog while a request runs. */
  confirmDisabled?: boolean
  onConfirm: () => void
  /** #373 — one value the dialog must have before it can confirm (the master password). The
   *  page owns the value; the dialog shows the field, keeps Confirm disabled while it is empty,
   *  and prints `error` under the field. */
  input?: ConfirmDialogInput
}

export interface ConfirmDialogInput {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string | null
  /** Defaults to 'password'. */
  type?: 'password' | 'text'
  placeholder?: string
  /** Defaults to 'new-password' so the browser never offers the user's own saved password. */
  autoComplete?: string
  /** When true the dialog may confirm with the field empty (the server says if it was needed). */
  optional?: boolean
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'destructive',
  confirmClassName,
  loading = false,
  confirmDisabled = false,
  onConfirm,
  input,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common')
  const inputId = useId()
  const errorId = `${inputId}-error`
  const blocked = loading || confirmDisabled || (input !== undefined && !input.optional && input.value.trim() === '')

  return (
    <Dialog.Root open={open} onOpenChange={loading ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ animationDuration: '200ms' }} />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1" style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Dialog.Title className="sr-only">{title}</Dialog.Title>

          <div className="flex flex-col items-center gap-4 text-center">
            {variant === 'destructive' && (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
            )}

            <div className="space-y-1.5">
              <h2 className="text-lg font-semibold">{title}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
            </div>

            {input && (
              <form
                className="w-full space-y-1.5 text-left"
                onSubmit={(e) => { e.preventDefault(); if (!blocked) onConfirm() }}
              >
                <label htmlFor={inputId} className="text-sm font-medium">{input.label}</label>
                <Input
                  id={inputId}
                  type={input.type ?? 'password'}
                  autoComplete={input.autoComplete ?? 'new-password'}
                  autoFocus
                  placeholder={input.placeholder}
                  value={input.value}
                  onChange={(e) => input.onChange(e.target.value)}
                  disabled={loading}
                  aria-invalid={input.error ? true : undefined}
                  aria-describedby={input.error ? errorId : undefined}
                />
                {input.error && (
                  <p id={errorId} role="alert" className="text-sm text-destructive">{input.error}</p>
                )}
              </form>
            )}

            <div className="flex w-full gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                {cancelLabel ?? t('actions.cancel')}
              </Button>
              <Button
                variant={variant}
                className={confirmClassName ? `flex-1 ${confirmClassName}` : 'flex-1'}
                onClick={onConfirm}
                disabled={blocked}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {confirmLabel ?? t('actions.confirm')}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
